import { TooltipButton } from '@/components/ui/TooltipButton'
import { ReactNode } from 'react'
import { CascadingFieldLabels } from '../../FormComponent/types'
import clsx from 'clsx'

type CascadingLevel = 'level1' | 'level2' | 'level3' | 'level4' | 'level5'

export interface CascadingValue {
  level1?: unknown
  level2?: unknown
  level3?: unknown
  level4?: unknown
  level5?: unknown
}

interface CascadingOption {
  value: unknown
  label: string | string[]
  options?: CascadingOption[]
}

/**
 * Finds the label for a cascading option at a specific level
 */
const findOptionLabel = (
  value: CascadingValue,
  level: CascadingLevel,
  options?: CascadingOption[]
): string | string[] | undefined => {
  if (!options) return undefined

  const { level1, level2, level3, level4 } = value
  const currentValue = value[level]

  switch (level) {
    case 'level1':
      return options.find((opt) => opt.value === currentValue)?.label

    case 'level2':
      return options.find((opt) => opt.value === level1)?.options?.find((opt) => opt.value === currentValue)?.label

    case 'level3':
      return options
        .find((opt) => opt.value === level1)
        ?.options?.find((opt) => opt.value === level2)
        ?.options?.find((opt) => opt.value === currentValue)?.label

    case 'level4':
      return options
        .find((opt) => opt.value === level1)
        ?.options?.find((opt) => opt.value === level2)
        ?.options?.find((opt) => opt.value === level3)
        ?.options?.find((opt) => opt.value === currentValue)?.label

    case 'level5':
      return options
        .find((opt) => opt.value === level1)
        ?.options?.find((opt) => opt.value === level2)
        ?.options?.find((opt) => opt.value === level3)
        ?.options?.find((opt) => opt.value === level4)
        ?.options?.find((opt) => opt.value === currentValue)?.label

    default:
      return undefined
  }
}

/**
 * Renders a single cascading level part
 */
const renderLevelPart = (value: unknown, optionLabel?: string | string[]): ReactNode => {
  if (Array.isArray(optionLabel)) {
    return <span>{`[ ${optionLabel.join(', ')} ]`}</span>
  }

  if (Array.isArray(value)) {
    return <span>{`[ ${value.join(', ')} ]`}</span>
  }

  if (optionLabel) {
    return <span>{`${optionLabel}`}</span>
  }

  if (value) {
    return <span>{`${value}`}</span>
  }

  return null
}

/**
 * Renders cascading field values with hierarchical display
 */
export const RenderCascadingField = ({
  value,
  fieldLabels,
  options,
  shouldTruncate,
}: {
  value: CascadingValue
  fieldLabels: CascadingFieldLabels
  options: CascadingOption[]
  shouldTruncate?: boolean
}): ReactNode => {
  const levels: CascadingLevel[] = ['level1', 'level2', 'level3', 'level4', 'level5']
  const parts: ReactNode[] = []

  for (const level of levels) {
    const currentValue = value[level]
    const levelLabel = fieldLabels[level]

    if (currentValue === undefined || !levelLabel) {
      continue
    }

    const optionLabel = findOptionLabel(value, level, options)
    const part = renderLevelPart(currentValue, optionLabel)

    if (part) {
      parts.push(part)
    }
  }

  if (parts.length === 0) return null

  const tooltip = Object.values(value)
    .filter((v) => v !== undefined)
    .join(' > ')

  const content = parts.map((part, i) => (
    <div className="flex gap-x-1" key={i}>
      <span className={clsx('font-medium', shouldTruncate && 'max-w-[60px] truncate')}>{part}</span>
      {i < parts.length - 1 ? ' > ' : ''}
    </div>
  ))

  return shouldTruncate ? (
    <TooltipButton tooltip={tooltip}>
      <div className="flex gap-1">{content}</div>
    </TooltipButton>
  ) : (
    <>{content}</>
  )
}
